/**
 * Canonical event registry following ObjectAction PascalCase convention.
 * All new events use this format. Legacy names are mapped below for dual-fire
 * during migration.
 */
export const MixpanelEvents = {
  // Acquisition & Onboarding
  AppOpened: 'AppOpened',
  AppResumed: 'AppResumed',
  AppInstalled: 'AppInstalled',
  SignupCompleted: 'SignupCompleted',
  OnboardingStarted: 'OnboardingStarted',
  OnboardingStepCompleted: 'OnboardingStepCompleted',
  OnboardingCompleted: 'OnboardingCompleted',

  // Content Engagement
  CourseStarted: 'CourseStarted',
  CourseFinished: 'CourseFinished',
  CourseStepStarted: 'CourseStepStarted',
  CourseStepFinished: 'CourseStepFinished',
  CourseAbandoned: 'CourseAbandoned',
  DailyRoutineStarted: 'DailyRoutineStarted',
  DailyRoutineFinished: 'DailyRoutineFinished',
  VideoViewed: 'VideoViewed',
  FavouriteSaved: 'FavouriteSaved',
  FavouriteRemoved: 'FavouriteRemoved',
  StreakAchieved: 'StreakAchieved',
  StreakBroken: 'StreakBroken',

  // Voice Assessment
  VoiceAssessmentStarted: 'VoiceAssessmentStarted',
  VoiceAssessmentResumed: 'VoiceAssessmentResumed',
  VoiceAssessmentMeasurementRecordingCompleted:
    'VoiceAssessmentMeasurementRecordingCompleted',
  VoiceAssessmentMeasurementQuestionsCompleted:
    'VoiceAssessmentMeasurementQuestionsCompleted',
  VoiceAssessmentMeasurementSubmitted: 'VoiceAssessmentMeasurementSubmitted',
  VoiceAssessmentCompleted: 'VoiceAssessmentCompleted',
  VoiceAssessmentAbandoned: 'VoiceAssessmentAbandoned',

  // Revenue & Subscription
  PaywallViewed: 'PaywallViewed',
  PaywallDismissed: 'PaywallDismissed',
  PurchaseCompleted: 'PurchaseCompleted',
  SubscriptionStarted: 'SubscriptionStarted',
  SubscriptionCancelled: 'SubscriptionCancelled',
  SubscriptionRenewed: 'SubscriptionRenewed',
  SubscriptionUpgraded: 'SubscriptionUpgraded',
  TrialExpired: 'TrialExpired',
  DiscountCodeApplied: 'DiscountCodeApplied',

  // Notifications & Errors
  PushPermissionGranted: 'PushPermissionGranted',
  PushPermissionDenied: 'PushPermissionDenied',
  NotificationOpened: 'NotificationOpened',
  PaymentFailed: 'PaymentFailed',
  AudioPlaybackError: 'AudioPlaybackError',
} as const

export type MixpanelEventName =
  (typeof MixpanelEvents)[keyof typeof MixpanelEvents]

/**
 * Maps canonical event names to their legacy Mixpanel string for dual-fire
 * during the dashboard migration window. Remove once dashboards are updated.
 */
export const LegacyEventNameMap: Partial<Record<MixpanelEventName, string>> = {
  [MixpanelEvents.AppOpened]: 'VocaliseOpened',
  [MixpanelEvents.AppInstalled]: 'AppInstallAttributed',
  [MixpanelEvents.DailyRoutineStarted]: 'StartedDailyRoutine',
  [MixpanelEvents.DailyRoutineFinished]: 'FinishedDailyRoutine',
  [MixpanelEvents.CourseStarted]: 'StartedCourse',
  [MixpanelEvents.CourseFinished]: 'FinishedCourse',
  [MixpanelEvents.CourseStepStarted]: 'StartedCourseStep',
  [MixpanelEvents.CourseStepFinished]: 'FinishedCourseStep',
  [MixpanelEvents.VideoViewed]: 'ViewedVideoLibrary',
  [MixpanelEvents.FavouriteSaved]: 'SavedFavourite',
  [MixpanelEvents.FavouriteRemoved]: 'RemovedFavourite',
}
